import { useMemo } from "react";
import { tileCsvUrl, type Tile } from "@/api/queries";
import { ArtifactCard } from "@/components/artifacts/ArtifactCard";
import { LinkedScope } from "@/components/artifacts/LinkedScope";
import type { Link } from "@/artifacts/links";
import { useGridScope } from "@/components/reports/gridScope";
import { ROWS, SPAN_COLUMNS, SPAN_ROWS } from "@/components/reports/gridFrames";
import { EmptyState } from "@/components/ui/EmptyState";
import { cn } from "@/lib/cn";

/**
 * A read-only grid: the same frames as the editable one, and nothing to press.
 *
 * The shared view and the front page's featured dashboard both show tiles a
 * reader should look at rather than rearrange. One component, so the two
 * cannot frame the same dashboard differently.
 */
export function ReadOnlyTiles({
  tiles,
  links = [],
  canFetch = false,
  emptyDetail = "This has no tiles to show.",
}: {
  tiles: Tile[];
  /** The grid's links (LINK-7): a selection in one tile reaches the others. */
  links?: Link[];
  /** A signed-in reader may fetch capped rows again; a share's reader may not. */
  canFetch?: boolean;
  emptyDetail?: string;
}) {
  const ordered = useMemo(
    () => [...tiles].sort((left, right) => left.sort_order - right.sort_order),
    [tiles],
  );
  const scope = useGridScope(tiles, { canFetch });
  if (!ordered.length) {
    return <EmptyState title="Nothing here yet" detail={emptyDetail} />;
  }
  return (
    <LinkedScope
      links={links}
      view={scope.view}
      drill={scope.drill}
      zoom={scope.zoom}
      label="Filter these tiles"
      className="flex flex-col gap-4"
    >
      <div className={cn("grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4", ROWS)}>
        {ordered.map((tile) => (
          <div
            key={tile.id}
            className={cn(
              "min-w-0",
              tile.col_span >= 3 ? "sm:col-span-2" : "",
              SPAN_COLUMNS[tile.col_span] ?? "lg:col-span-2",
              SPAN_ROWS[tile.row_span] ?? "sm:row-span-2",
            )}
          >
            <ArtifactCard
              artifact={tile.artifact}
              linkIndex={scope.indexOf(tile.id)}
              csvUrl={tileCsvUrl(tile.id)}
              className="shadow-card h-full"
              fillChart
              compact
              bodyClassName="overflow-auto"
              subtitle={
                tile.refresh_status === "error" || tile.refresh_status === "blocked" ? (
                  <span className="type-caption text-warning normal-case">
                    Could not be refreshed — showing the last values that worked.
                  </span>
                ) : undefined
              }
            />
          </div>
        ))}
      </div>
    </LinkedScope>
  );
}
